import styled, { css } from 'styled-components';
import { inputContainerStyleOverride } from '../../shared';

export const Content = styled.div`
  box-sizing: border-box;
  border-radius: 8px;
  background: ${({ theme }) => theme.colors.bg.secondary};
  color: ${({ theme }) => theme.colors.fg.primary};
  width: 352px;
  max-width: calc(100vw - 32px);
  max-height: calc(100vh - 64px);
  max-height: calc(100dvh - 64px);
  overflow: hidden;
  user-select: none;
  display: flex;
  flex-direction: column;
  font-size: 13px;
  line-height: 1.4;
  box-shadow: 0 12px 32px rgba(0, 0, 0, 0.24);
`;

export const Body = styled.div`
  flex: 1 1 auto;
  overflow-y: auto;
  overscroll-behavior: contain;
  min-height: 0;
  padding: 16px;
  scrollbar-width: thin;
  scrollbar-color: ${({ theme }) => theme.colors.fg.tertiary} transparent;
`;

export const Tabs = styled.div`
  display: flex;
  flex-shrink: 0;
  margin: 0 16px;
  padding: 0;
  border-bottom: 1px solid ${({ theme }) => theme.colors.divider.secondary};
`;

export const Tab = styled.button`
  flex: 1;
  padding: 10px 8px;
  border: 0;
  border-bottom: 2px solid transparent;
  background: transparent;
  color: ${({ theme }) => theme.colors.fg.secondary};
  font: inherit;
  cursor: pointer;
  &[aria-selected='true'] {
    color: ${({ theme }) => theme.colors.fg.primary};
    border-bottom-color: ${({ theme }) => theme.colors.fg.linkNormal};
    font-weight: 500;
  }
  &:hover {
    background: ${({ theme }) => theme.colors.bg.tertiary};
  }
  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.fg.linkNormal};
    outline-offset: -2px;
  }
`;

export const Group = styled.div`
  display: grid;
  gap: 12px;
  & + & {
    margin-top: 16px;
    padding-top: 16px;
    border-top: 1px solid ${({ theme }) => theme.colors.divider.secondary};
  }
`;

export const editableFieldStyle = css`
  background: ${({ theme }) => theme.colors.bg.secondary};
  border-radius: ${({ theme }) => theme.borders.radius.small};
  &:hover {
    border-color: ${({ theme }) => theme.colors.border.defaultHover};
  }
  ${inputContainerStyleOverride};
`;

export const FieldRow = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 180px);
  align-items: center;
  gap: 10px;
  min-width: 0;
`;

export const NumericRow = styled(FieldRow)`
  grid-template-columns: 68px minmax(0, 1fr) 80px;
`;

export const SliderContainer = styled.div`
  min-width: 0;
  input[type='range'] {
    width: 100%;
    min-width: 0;
  }
`;

export const CompactFieldRow = styled(FieldRow)`
  grid-template-columns: minmax(0, 1fr) 80px;
`;

export const PreviewArea = styled.div`
  margin: 0 16px 12px;
  padding: 10px 12px;
  background: ${({ theme }) => theme.colors.bg.primary};
  border-radius: ${({ theme }) => theme.borders.radius.small};
  font-size: 12px;
  color: ${({ theme }) => theme.colors.fg.secondary};
  svg {
    display: block;
    margin: 4px auto 0;
    width: 48px;
    height: 48px;
    border: 0;
  }
`;

export const ColorRow = styled(FieldRow)`
  grid-template-columns: minmax(0, 1fr) 190px;
`;

export const ColorControls = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) 70px;
  gap: 6px;
  min-width: 0;
`;

export const ColorValue = styled.div`
  display: flex;
  align-items: center;
  height: 36px;
  min-width: 0;
  border: 1px solid ${({ theme }) => theme.colors.border.defaultNormal};
  border-radius: ${({ theme }) => theme.borders.radius.small};
  background: ${({ theme }) => theme.colors.bg.secondary};
  &:hover {
    border-color: ${({ theme }) => theme.colors.border.defaultHover};
  }
`;

export const colorHexStyle = css`
  border: 0;
  padding: 4px 6px 4px 0;
  background: transparent;
  ${inputContainerStyleOverride};
`;

export const ColorSwatch = styled.input`
  width: 32px;
  height: 32px;
  flex-shrink: 0;
  padding: 5px;
  border: 0;
  border-radius: ${({ theme }) => theme.borders.radius.small};
  background: transparent;
  cursor: pointer;
  &::-webkit-color-swatch-wrapper {
    padding: 0;
  }
  &::-webkit-color-swatch {
    border: 1px solid ${({ theme }) => theme.colors.border.defaultNormal};
    border-radius: 2px;
  }
  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.border.focus};
    outline-offset: 2px;
  }
`;

export const ButtonAnchor = styled.span`
  display: flex;
`;

export const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 12px 16px;
  flex-shrink: 0;
`;

export const EnableLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
`;

export const Summary = styled.span`
  color: ${({ theme }) => theme.colors.fg.secondary};
  font-size: 12px;
`;

export const Notice = styled(Summary)`
  display: block;
  margin: 12px 0;
`;

export const Heading = styled.h3`
  color: ${({ theme }) => theme.colors.fg.primary};
  font-size: 13px;
  font-weight: 500;
  margin: 0;
`;

export const Title = styled(Heading)`
  font-size: 14px;
`;

export const Actions = styled.div`
  padding: 12px 16px;
  border-top: 1px solid ${({ theme }) => theme.colors.divider.secondary};
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  justify-content: space-between;
  flex-shrink: 0;
`;

// Dropdown menus are portaled by the design system. Treat only menus owned by
// this popup's expanded controls as part of its focus/dismissal boundary.
export function isEffectMenuTarget(container, target) {
  if (!target || !container) return false;
  return Array.from(
    container.querySelectorAll('[aria-expanded="true"][aria-owns]')
  ).some((trigger) =>
    document.getElementById(trigger.getAttribute('aria-owns'))?.contains(target)
  );
}
